import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { IconButton, Popover } from '@mui/material';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';

import { onKeyDownHandler } from '@/shared/utils/a11y';

import styles from './index.module.scss';

const acceptableUsePolicyUrl = 'https://www.digitalrealty.com/acceptable-use-policy/';
const privacyPolicyUrl = 'https://www.digitalrealty.com/privacy';

const supportEmail = 'customercare@digitalrealty.com';

type PhoneNumber = {
  readonly tel: string;
  readonly label: string;
  readonly note?: string;
};

type Region = {
  readonly name: string;
  readonly numbers: readonly PhoneNumber[];
};

const customerSuccessRegions: readonly Region[] = [
  {
    name: 'Australia',
    numbers: [{ tel: '+61383611501', label: '+61 383611501' }],
  },
  {
    name: 'Europe',
    numbers: [
      { tel: '+8000183909', label: '+8000 183 909' },
      { tel: '+18622778813', label: '+1 862 277 8813' },
    ],
  },
  {
    name: 'Hong Kong',
    numbers: [{ tel: '+85222817502', label: '+852 22817502' }],
  },
  {
    name: 'Japan',
    numbers: [
      { tel: '+81726299066', label: '+81-726299066', note: 'English' },
      { tel: '+81727368473', label: '+81-727368473', note: 'Japanese' },
    ],
  },
  {
    name: 'North America',
    numbers: [{ tel: '+18773577782', label: '+1 877 357 7782' }],
  },
  {
    name: 'Singapore',
    numbers: [{ tel: '+6565053912', label: '+65 65053912' }],
  },
];

const SupportLinks = () => {
  const [phoneListAnchor, setPhoneListAnchor] = useState<HTMLElement>();
  const isPhoneListOpen = Boolean(phoneListAnchor);

  const closePhoneList = () => {
    setPhoneListAnchor(undefined);
  };

  const togglePhoneList = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    if (isPhoneListOpen) {
      closePhoneList();
      return;
    }

    setPhoneListAnchor(event.currentTarget);
  };

  return (
    <div className={styles.support}>
      <p className={styles.heading}>Need help? Contact our support team.</p>
      <a className={styles.contact} href={`mailto:${supportEmail}`}>
        <MailOutlineIcon fontSize="small" />
        {supportEmail}
      </a>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isPhoneListOpen}
        aria-haspopup="dialog"
        className={styles.contact}
        onClick={togglePhoneList}
        onKeyDown={onKeyDownHandler(togglePhoneList)}
      >
        <PhoneOutlinedIcon fontSize="small" />
        Customer Success phone numbers
      </div>
      <Popover
        open={isPhoneListOpen}
        anchorEl={phoneListAnchor}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            className: styles.popover,
            elevation: 0,
          },
        }}
        onClose={closePhoneList}
      >
        <div className={styles.popoverHeader}>
          <span>Customer Success</span>
          <IconButton
            aria-label="Close"
            size="small"
            className={styles.closeButton}
            onClick={closePhoneList}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <ul className={styles.regionList}>
          {customerSuccessRegions.map((region) => (
            <li key={region.name} className={styles.region}>
              <div className={styles.regionName}>{region.name}</div>
              {region.numbers.map((number) => (
                <div key={number.tel} className={styles.numberRow}>
                  <a href={`tel:${number.tel}`}>{number.label}</a>
                  {number.note && <span className={styles.note}>({number.note})</span>}
                </div>
              ))}
            </li>
          ))}
        </ul>
      </Popover>
      <div className={styles.policies}>
        <a href={acceptableUsePolicyUrl} target="_blank" rel="noopener noreferrer">
          Acceptable Use Policy
        </a>
        <span className={styles.separator} aria-hidden="true">
          |
        </span>
        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
          Privacy
        </a>
      </div>
    </div>
  );
};

export default SupportLinks;
